import { useRef, useState } from 'react'

import type {
  ApiCallOptions,
  ApiOrder,
  GetListApi,
  GetListApiResponse,
} from '../api'
import { getApi, isApiNotFoundError, useCreateApiAbortSignal } from '../api'
import { getOrderParams } from '../utils/api'
import type { PaginationValue } from '../utils/pagination'
import {
  getPaginationApiParams,
  PAGINATION_ALL_ITEMS_VALUE,
  updatePaginationCount,
  updatePaginationPage,
} from '../utils/pagination'
import type { ReactRefObject, ReactStateSetter } from '../utils/react'

import { useConstantCallback } from './useConstantCallback'
import { useVariableRef } from './useVariableRef'

/**
 * Table loader.
 * `TItem` is an object returned by server,
 * `TParams` is a type describing the parameters accepted by the server.
 */
export type TableLoader<TItem, TParams> = {
  /**
   * The last error that occurred during the request.
   */
  error: Nullable<Error>

  /**
   * Indicates that the data has not yet been downloaded.
   */
  initial: boolean

  /**
   * An array of items from server.
   */
  items: TItem[]

  /**
   * Loading state.
   */
  loading: boolean

  /**
   * Callback function to load initial page.
   * {@link loadInitial} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   *
   * @param params - params
   * @param pagination - pagination configurations (if omitted, the options for loading all data is used)
   */
  loadInitial: (
    params: TParams,
    pagination?: PaginationValue | null,
    order?: ApiOrder | null
  ) => Nullable<Promise<GetListApiResponse<TItem>>>

  /**
   * Callback function to load page.
   * {@link loadPage} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   *
   * @param pagination - pagination configurations (if omitted, the options for loading all data is used)
   */
  loadPage: (
    pagination?: PaginationValue | null,
    order?: ApiOrder | null
  ) => Nullable<Promise<GetListApiResponse<TItem>>>

  /**
   * Reference to the last call order.
   */
  orderRef: ReactRefObject<Nullable<ApiOrder>>

  /**
   * Reference to the last call pagination.
   */
  paginationRef: ReactRefObject<Nullable<PaginationValue>>

  /**
   * Reference to the last call params.
   */
  paramsRef: ReactRefObject<Nullable<TParams>>

  /**
   * Function to set error.
   * {@link setError} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   */
  setError: ReactStateSetter<Nullable<Error>>

  /**
   * Function to set initial state
   * {@link setInitial} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   */
  setInitial: ReactStateSetter<boolean>

  /**
   * Function to set items.
   * {@link setItems} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   */
  setItems: ReactStateSetter<TItem[]>

  /**
   * Function to set loading state.
   * {@link setLoading} identity is stable and won’t change on re-renders.
   * This is why it’s safe to omit from the `useEffect` or `useCallback`
   * dependency list.
   */
  setLoading: ReactStateSetter<boolean>
}

/**
 * Table loader props.
 */
export type TableLoaderOptions<TItem> = Omit<ApiCallOptions, 'abortSignal'> & {
  /**
   * Function to invoke api call.
   * Default: `getApi` from `@api`.
   */
  getApi: Nullable<GetListApi<TItem>>

  /**
   * Callback function to update pagination.
   */
  setPagination: Nullable<(pagination: PaginationValue) => void>
}

/**
 * ```
 *  const [pagination, setPagination] =
 *      useState<PaginationValue>({ count: 0, page: 0, perPage: 10 })
 *
 *  const {
 *    error,
 *    initial,
 *    items,
 *    loading,
 *    loadInitial,
 *    loadPage,
 *    paramsRef,
 *    setError,
 *    setInitial,
 *    setItems,
 *    setLoading,
 *    setParams
 *  } = useTableLoader<Item>({
 *    getApi: (...args) => getApi('/path/to/endpoint', ...args),
 *    setPagination
 *  });
 * ```
 *
 * Returns an object to load table data.
 */
export function useTableLoader<TItem, TParams>(
  props: TableLoaderOptions<TItem>
): TableLoader<TItem, TParams> {
  const propsRef = useVariableRef<TableLoaderOptions<TItem>>({
    getApi,
    ...props,
  })

  const orderRef = useRef<ApiOrder | undefined | null>(null)
  const paramsRef = useRef<TParams | undefined | null>(null)
  const paginationRef = useRef<PaginationValue | undefined | null>(null)

  const [initial, setInitial] = useState(true)
  const [loading, setLoading] = useState(true)

  const [error, setError] = useState<Error | undefined | null>(null)
  const [items, setItems] = useState<TItem[]>([])

  const createLoadAbortSignal = useCreateApiAbortSignal()

  const loadPage = useConstantCallback(
    (
      pagination: PaginationValue | undefined | null = null,
      order: ApiOrder | undefined | null = null
    ): Promise<GetListApiResponse<TItem> | undefined | null> => {
      const { getApi, setPagination, ...options } = propsRef.current

      paginationRef.current = pagination
      orderRef.current = order

      setError(null)
      setLoading(true)

      return getApi({
        options: {
          ...options,
          abortSignal: createLoadAbortSignal(),
          ignore404Redirect: pagination?.page > 0,
        },
        params: {
          ...paramsRef.current,
          ...getPaginationApiParams(pagination ?? PAGINATION_ALL_ITEMS_VALUE),
          ...getOrderParams(order),
        },
      })
        .then((response?: GetListApiResponse<TItem> | null) => {
          if (response) {
            const nextPagination = updatePaginationCount(
              pagination,
              response.count
            )

            setInitial(false)
            setItems(response.results)

            paginationRef.current = nextPagination
            setPagination?.(nextPagination)
          }
        })
        .catch((error: Error) => {
          if (isApiNotFoundError(error) && pagination?.page > 0) {
            // Load first page if server returns 404
            return loadPage(updatePaginationPage(pagination, 0), order)
          }

          setError(error)
        })
        .finally(() => setLoading(false))
    }
  )

  const loadInitial = useConstantCallback(
    (
      params: TParams,
      pagination: PaginationValue | undefined | null = null,
      order: ApiOrder | undefined | null = null
    ): Promise<GetListApiResponse<TItem> | undefined | null> => {
      setInitial(true)
      paramsRef.current = params

      return loadPage(pagination, order)
    }
  )

  return {
    error,
    initial,
    items,
    loading,
    loadInitial,
    loadPage,
    orderRef,
    paginationRef,
    paramsRef,
    setError,
    setInitial,
    setItems,
    setLoading,
  }
}
